import React, { useRef } from 'react';
import { X, Printer, Loader } from 'lucide-react';
import { useGetDonneesReleveQuery } from '../../services/relevesApi';
import { RelevePreview, imprimerRelevePage } from '../../components/Releves/RelevePreview';

interface Props {
  matricule: string;
  semestre: number;
  anneeAcademique: string;
  onClose: () => void;
}

const ModalReleveProvisoire: React.FC<Props> = ({ matricule, semestre, anneeAcademique, onClose }) => {
  const { data, isLoading, error } = useGetDonneesReleveQuery({
    matricule, semestre, annee_academique: anneeAcademique,
  });
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = async () => {
    // Fix 2026-09-14 : relevé imprimé seul sur une feuille A4 ajustée (le bas était coupé)
    const page = printRef.current?.querySelector<HTMLElement>('.rn-page');
    if (!page) { window.print(); return; }
    try {
      await imprimerRelevePage(page, `Releve_provisoire_${matricule}_S${semestre}`);
    } catch {
      window.print();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 print:relative print:bg-transparent print:p-0">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col print:max-h-none print:max-w-full print:shadow-none print:rounded-none">
        {/* Header (masqué à l'impression) */}
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-between print:hidden">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            Relevé provisoire · {matricule} · Semestre {semestre}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={!data}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg"
            >
              <Printer size={14} /> Imprimer
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-4 print:p-0" ref={printRef}>
          {isLoading && (
            <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400">
              <Loader className="animate-spin mr-2" size={18} />
              Chargement des données du relevé…
            </div>
          )}
          {error && (
            <div className="p-8 text-center text-red-600 dark:text-red-400">
              Erreur de chargement : {(error as any)?.status || 'inconnu'}
              {(error as any)?.data?.error && ` - ${(error as any).data.error}`}
            </div>
          )}
          {data && (
            <RelevePreview data={data} typeReleve="provisoire" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalReleveProvisoire;
